import { useEffect, useRef } from "react";

import { parseLatex } from "@ever-tutor/et-ui";

import { useStudentInputStore } from "@/features/tutor/store/useStudentInputStore";
import { serializeMixedDocument } from "@/features/tutor/utils/serializeMixed";

export const joinInputValues = (inputs: Record<string, string>): string =>
  Object.values(inputs)
    .map((value) => value.trim())
    .filter(Boolean)
    .join(", ");

interface MirrorTarget {
  setText: (text: string) => void;
  /** What the composer currently holds, serialized. */
  text: string;
  isEmpty: boolean;
}

/**
 * Reflects the slide's input boxes into the chat composer.
 *
 * Only while the composer still holds what this last wrote: setText replaces
 * the whole document, so mirroring unconditionally threw away anything the
 * student had typed the moment they touched a box.
 */
export const useInputBoxMirror = ({
  setText,
  text,
  isEmpty,
}: MirrorTarget): void => {
  const joined = useStudentInputStore((state) => joinInputValues(state.inputs));
  const mirrored = useRef(joined);
  const written = useRef<string | null>(null);

  useEffect(() => {
    if (joined === mirrored.current) return;
    mirrored.current = joined;

    if (!isEmpty && text !== written.current) return;

    written.current = serializeMixedDocument(parseLatex(joined));
    setText(joined);
  }, [joined, text, isEmpty, setText]);
};
